import { FC, ReactElement, Suspense, lazy, useCallback, useMemo, useState } from 'react';

import MainMenu from '../../NavDrawer/MainMenu/MainMenu';
import TabItem from './TabItem';
import { ILinks } from '../utils/constants/routes.interface';
import { getLinks } from '../utils/getLinks';
import IngestionDropdown from 'Modules/Studio/components/StudioInfo/Ingestion/IngestionStandaloneDropdown/IngestionDropdown';
import useDismissibleLinks from '../hooks/useDismissibleLinks';
import { useAppSelector } from '@redux/store';
import { ChunkErrorBoundary, ErrorBoundary } from '@components/ErrorBoundary';

const IsStudioHOC = lazy(() => import('Modules/Studio/components/StudioInfo/IsStudioHOC'));

type Props = {
  links: ILinks[];
  path?: string;
};

const TabBar: FC<Props> = ({ links, path }): ReactElement<any> => {
  const { dismissibleLinks, removeDismissibleLink, addDismissibleLink } = useDismissibleLinks();
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(false);
  const permissions = useAppSelector((state) => state.roles.permissions);

  const tabItems = useMemo(() => getLinks(links, permissions), [links, permissions]);

  const onDismiss = useCallback(
    (name: string) => {
      if (path && removeDismissibleLink) {
        removeDismissibleLink(path, name);
      }
    },
    [path, removeDismissibleLink]
  );

  const pathSpecificDismissibleLinks = useMemo(() => {
    if (!path) return [];
    const links: ILinks[] = [];
    Array.from(dismissibleLinks.entries())
      .filter(([key, _]) => key.includes(path))
      .forEach(([_, value]) => {
        links.push(value);
      });
    return links;
  }, [dismissibleLinks, path]);

  return (
    <header>
      <nav className="tw:bg-primary-200 tw:flex tw:justify-between">
        <div className="tw:flex">
          <MainMenu isOpen={isMainMenuOpen} setIsOpen={setIsMainMenuOpen} isBentoCircle={false} />
          <div className="tw:flex">
            {tabItems.map((props, index) => (
              <TabItem {...props} key={index} />
            ))}
            {pathSpecificDismissibleLinks.map((link, index) => (
              <TabItem
                testId={link.tabItem.testId}
                to={link.tabItem.to}
                key={index}
                name={link.tabItem.name}
                label={link.tabItem.label}
                variant={link.tabItem.variant}
                isDismissible={true}
                onDismiss={() => onDismiss((link.tabItem.name as string)?.toLowerCase() || '')}
              />
            ))}
          </div>
        </div>
        <ErrorBoundary>
          <ChunkErrorBoundary>
            <Suspense>
              <IsStudioHOC>
                <IngestionDropdown addDismissibleLink={addDismissibleLink} />
              </IsStudioHOC>
            </Suspense>
          </ChunkErrorBoundary>
        </ErrorBoundary>
      </nav>
    </header>
  );
};

export default TabBar;
